<template>
  <div id="app">
    <div id="main">
      <div id="controls" :class="{ 'controls--wide': readingMode }">
        <keep-alive>
          <ModelForm
            class="model-form"
            :class="{ hidden: readingMode }"
            :value.sync="modelName"
          />
        </keep-alive>
        <transition name="fade-in">
          <h3 v-if="readingMode" class="title">
            IMPGAN
          </h3>
        </transition>
        <div class="tab-group">
          <div class="tab-group__border" />
          <button
            type="button"
            @click="changeTab('manual')"
            :class="{ 'tab--active': currentTab === 'manual' }"
            class="tab"
          >
            Manual
          </button>
          <button
            type="button"
            @click="changeTab('auto')"
            :class="{ 'tab--active': currentTab === 'auto' }"
            class="tab"
          >
            Auto
          </button>
          <button
            type="button"
            @click="changeTab('video')"
            :class="{ 'tab--active': currentTab === 'video' }"
            class="tab"
          >
            Video
          </button>
          <button
            type="button"
            @click="changeTab('about')"
            :class="{ 'tab--active': currentTab === 'about' }"
            class="tab"
          >
            What?
          </button>
          <button
            type="button"
            @click="changeTab('contact')"
            :class="{ 'tab--active': currentTab === 'contact' }"
            class="tab"
          >
            Who?
          </button>
        </div>
        <keep-alive>
          <Form
            v-if="currentTab === 'manual'"
            :is-generating="isGenerating"
            @update="onFormUpdate"
          />
          <InterpolationForm
            :model-name="modelName"
            v-if="currentTab === 'video'"
            :is-generating="isGenerating"
            @update="onFormUpdate"
          />
          <MixForm
            :model-name="modelName"
            v-if="currentTab === 'auto'"
            :is-generating="isGenerating"
            @update="onFormUpdate"
          />
        </keep-alive>
        <About v-if="currentTab === 'about'" />
        <Contact v-if="currentTab === 'contact'" />
      </div>
      <transition name="fade">
        <keep-alive>
          <Canvas
            v-if="!readingMode"
            @generating="onGeneration"
            :model-name="modelName"
            :config="config"
          />
        </keep-alive>
      </transition>
    </div>
  </div>
</template>

<script>
import "normalize.css"

import Form from "./components/Form.vue"
import ModelForm from "./components/ModelForm.vue"
import InterpolationForm from "./components/InterpolationForm.vue"
import MixForm from "./components/MixForm.vue"
import Canvas from "./components/Canvas.vue"
import About from "./components/About.vue"
import Contact from "./components/Contact.vue"

const defaultValues = {
  currentTab: "auto",
  config: null,
  modelName: "celeb_hq",
  isGenerating: false
}

export default {
  name: "app",
  components: {
    About,
    Contact,
    Canvas,
    Form,
    MixForm,
    InterpolationForm,
    ModelForm
  },
  data() {
    return {
      ...defaultValues
    }
  },
  computed: {
    readingMode() {
      return ["about", "contact"].includes(this.currentTab)
    }
  },
  methods: {
    changeTab(tab) {
      this.$ga.page(`/${tab}`)
      this.currentTab = tab
    },
    onFormUpdate(config) {
      this.config = config
    },
    onGeneration(isGenerating) {
      this.isGenerating = isGenerating
    }
  }
}
</script>

<style lang="scss">
@import "./style/mixins.scss";
@import "./style/rules.scss";
@import "./style/variables.scss";

body {
  background-color: $color-dark-darkest;

  background: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)),
    url("./style/background.png");

  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen",
    "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue",
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  color: $color-white;
  display: flex;
  flex-direction: column;
}

#app {
  margin-top: 40px;
  @include centered-flex;
  flex-direction: column;
}

#main {
  width: 95%;
  display: flex;
  justify-content: flex-start;
  overflow-x: hidden;
}

.model-form {
  transform: translateY(0);
  transition: 1s all;
  opacity: 1;
}

.hidden {
  transition: 1s all;
  opacity: 0;
  transform: translateY(-100%);
}

.forms {
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column;
}

/* Default style reset */
fieldset {
  padding: 0;
  margin: 0;
  border: none;
}

label {
  @include disable-select;
}

.form-button {
  background-color: $color-dark;
  padding: 3px;
  border: 0.5px solid white;
  color: white;
  transition-duration: 200ms;
  border-radius: 3px;
  box-shadow: 1px 1px 1px 1px rgba(0, 0, 0, 0.25);
  height: 40px;
}

.form-button:active {
  background: lightgrey;
}

select {
  border: none;
  background-color: white;
  outline: none;
  tap-highlight-color: rgba(0, 0, 0, 0);
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  padding: 3px;
  border: 0.5px solid black;
  transition-duration: 200ms;
  border-radius: 3px;
  box-shadow: 1px 1px 1px 1px rgba(0, 0, 0, 0.25);
  height: 40px;
}

select:active {
  background: lightgrey;
}

#controls {
  position: relative;
  min-width: 400px;
  width: 30%;
  align-self: flex-start;
}

.title {
  letter-spacing: 3px;
  text-shadow: 3px 3px 1px rgba(220, 220, 220, 0.3);
  font-size: 3em;
  align-self: center;
  position: absolute;
  margin-left: $margin;
  margin-top: -70px;
}

#controls.controls--wide {
  min-width: 50%;
}

.tab-group__border {
  position: absolute;
  top: 100.5%;
  width: 100%;
  align-self: flex-start;
  border-top: 2px solid $color-dark;
}

.tab-group {
  max-width: 350px;
  position: relative;
  @include centered-flex;
  margin: 0 $margin;
  background-color: $color-dark;
  border-radius: $border-radius $border-radius 0 0;
  border: 2px solid #ededed;
  border-bottom: none;
}

.tab {
  @include centered-flex;
  width: 100%;
  padding: 10px;
  background-color: $color-dark-lightest;
  box-shadow: none;
  border-bottom: none;
  text-transform: uppercase;
  font-size: 0.9em;
  color: rgba(255, 255, 255, 0.3);
}

.tab:first-child {
}

.tab:nth-child(5),
.tab:nth-child(6) {
  font-style: oblique;
}

.tab--active {
  transition: all 0.3s;
  color: white;
  background-color: $color-dark;
  font-weight: bold;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 1s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.fade-in-enter,
.fade-in-leave-to {
  opacity: 0;
}

.fade-in-enter-active {
  transition: opacity ease-in 1s;
}

.fade-in-leave-active {
  transition: opacity ease-out 1s;
}

@media (max-width: 767px) {
  #main {
    height: 100%;
    width: 98%;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }

  .tab-group__border {
    display: none;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
    transform: translateY(100%);
  }

  .tab-group {
    width: unset;
    max-width: unset;
  }

  #controls {
    min-width: 100%;
    max-width: 100%;
  }

  #controls.controls--wide {
    min-width: 100%;
    max-width: 100%;
  }

  .form-button {
    height: 50px;
  }

  .forms {
    width: 100%;
    flex-direction: column;
  }
}
</style>
